import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
  lazy,
  Suspense,
} from "react";
import "./RegistryIndicatorsDropdown.css";
import indicatorRegistry from "../../Registry/Indicators/IndicatorRegistry";
import {
  sortedIndicatorEntries,
  silverlightIndicatorList,
} from "../../Registry/Indicators/registryListing";
import {
  silverlightOrigin,
  isFromSilverlight,
} from "../../Registry/Indicators/silverlightOrigin";
import dropdownIconUp from "../../img/dropdown-icon-up.svg";
import { useChartsStore } from "../../store/chartStore";
import { useFavoriteIndicatorsStore } from "../../store/favoriteIndicatorsStore";
import { useCompareStore } from "../../store/compare";
import { createIndicatorInstance } from "../../utils/indicatorInstance.js";
import { indicatorsPickerRef } from "./indicatorsPickerRef";
import SimpleModal from "../Modals/SimpleModal";
import marketdata from "../../data/Services/marketdata";
import getChartHostBody from "~/utils/chartHostBody";
// Lazy load heavy modal
const IndicatorEditModal = lazy(() => import("../Modals/IndicatorEditModal"));

// Empty array constant to prevent infinite re-renders
const EMPTY_COMPARISONS = [];

// Pill de procedência — helper de render (sem estado), nulo p/ não-migrados
const renderSilverlightPill = (indicatorKey) =>
  isFromSilverlight(indicatorKey) ? (
    <span
      className="silverlight-pill"
      title={`Migrado do app Silverlight (${silverlightOrigin[indicatorKey]})`}
    >
      Silverlight
    </span>
  ) : null;

// Common symbols for quick compare access
const COMMON_SYMBOLS = [
  "WINFUT",
  "WDOFUT",
  "PETR4",
  "VALE3",
  "ITUB4",
  "BBDC4",
  "ABEV3",
  "B3SA3",
  "MGLU3",
  "LREN3",
  "RENT3",
  "GGBR4",
];

const RegistryIndicatorsDropdown = () => {
  const selectedChart = useChartsStore((state) => state.getSelectedChart());
  const selectedChartId = selectedChart?.id || null;
  const chartIndicators = selectedChart?.indicators || [];
  const chartSymbol = selectedChart?.symbol || "";
  const chartPeriodicity = selectedChart?.periodicity || "1min";
  const addIndicatorToChart = useChartsStore(
    (state) => state.addIndicatorToChart,
  );

  const favorites = useFavoriteIndicatorsStore((state) => state.favorites);
  const toggleFavorite = useFavoriteIndicatorsStore(
    (state) => state.toggleFavorite,
  );

  // Compare store - access comparisons state directly
  const comparisons = useCompareStore((state) => state.comparisons);
  const comparisonSymbols =
    comparisons[selectedChartId]?.symbols || EMPTY_COMPARISONS;
  const isCompareMode = comparisonSymbols.length > 0;
  const addComparisonSymbol = useCompareStore(
    (state) => state.addComparisonSymbol,
  );
  const removeComparisonSymbol = useCompareStore(
    (state) => state.removeComparisonSymbol,
  );
  const clearComparisons = useCompareStore((state) => state.clearComparisons);

  // Use local state instead of Zustand to avoid re-render issues
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  // Expose the picker to the chart context menu ("Estudos…" opens THIS modal —
  // the menu focuses the clicked chart first, so getSelectedChart() targets it).
  useEffect(() => {
    indicatorsPickerRef.current = { open: () => setDropdownOpen(true) };
    return () => {
      indicatorsPickerRef.current = null;
    };
  }, []);

  // Compare state
  const [compareSearchTerm, setCompareSearchTerm] = useState("");
  const [compareLoading, setCompareLoading] = useState(false);
  const [compareError, setCompareError] = useState(null);
  const [filteredCompareSymbols, setFilteredCompareSymbols] = useState([]);
  const [compareSelectedIndex, setCompareSelectedIndex] = useState(0);
  const compareSearchInputRef = useRef(null);

  // Indicator edit modal state
  const [modalVisible, setModalVisible] = useState(false);
  const [modalIndicatorEntry, setModalIndicatorEntry] = useState(null);
  // Janela onde o gráfico alvo vive — resolvida no clique, nunca no render (#375).
  const [modalHostBody, setModalHostBody] = useState(null);
  const [modalCategoryKey, setModalCategoryKey] = useState(null);

  const buttonRef = useRef(null);
  const indicatorListRef = useRef(null);
  const searchInputRef = useRef(null);

  // Detect platform for keyboard shortcuts display
  const modKey = navigator.platform.includes("Mac") ? "⌘" : "Ctrl";

  // Auto-focus search input when dropdown opens (skip on touch devices to avoid IME toolbar)
  useEffect(() => {
    if (dropdownOpen && searchInputRef.current) {
      const isTouchDevice = window.matchMedia("(pointer: coarse)").matches;
      if (!isTouchDevice) {
        setTimeout(() => {
          searchInputRef.current?.focus();
        }, 100);
      }
    }
  }, [dropdownOpen]);

  // Default to favorites category when opening
  useEffect(() => {
    if (dropdownOpen && favorites.length > 0 && selectedCategory === null) {
      setSelectedCategory("favoritos");
    }
  }, [dropdownOpen, favorites.length, selectedCategory]);

  // Memoize categories: "Favoritos" (se houver) no topo, grupos reais na ordem
  // do registry, pseudo-grupo "Silverlight" como ÚLTIMO grupo, "Comparar" no fim
  const categories = useMemo(() => {
    const baseCategories = Object.keys(indicatorRegistry);
    const cats =
      favorites.length > 0 ? ["favoritos", ...baseCategories] : baseCategories;
    return [...cats, "silverlight", "comparar"];
  }, [favorites]);

  // Filter out main symbol and already added symbols for compare quick list
  const availableCompareSymbols = useMemo(() => {
    return COMMON_SYMBOLS.filter(
      (s) =>
        s !== chartSymbol && !comparisonSymbols.some((c) => c.symbol === s),
    );
  }, [chartSymbol, comparisonSymbols]);

  // Filter symbols for compare lookup as user types
  useEffect(() => {
    if (compareSearchTerm.length >= 1) {
      const loadedSymbols = marketdata.symbols;
      if (loadedSymbols && loadedSymbols.size > 0) {
        const symbols = Array.from(loadedSymbols, ([key, value]) => ({
          symbol: key,
          ...value,
        }));

        // Filter symbols that match search and are not already added
        const filtered = symbols
          .filter(
            (s) =>
              s.I.toLowerCase().startsWith(compareSearchTerm.toLowerCase()) &&
              s.symbol !== chartSymbol &&
              !comparisonSymbols.some((c) => c.symbol === s.symbol),
          )
          .slice(0, 10); // Limit to 10 results

        setFilteredCompareSymbols(filtered);
        setCompareSelectedIndex(0);
      } else {
        setFilteredCompareSymbols([]);
      }
    } else {
      setFilteredCompareSymbols([]);
      setCompareSelectedIndex(0);
    }
  }, [compareSearchTerm, chartSymbol, comparisonSymbols]);

  // Only real registry categories for search/filter logic
  const registryCategories = useMemo(() => Object.keys(indicatorRegistry), []);

  // Memoize filtered indicators (avoid duplicates); parte das entradas já
  // ordenadas para o resultado sair alfabético dentro de cada categoria
  const filteredIndicators = useMemo(() => {
    if (!searchTerm) return {};
    const seen = new Set();
    const term = searchTerm.toLowerCase();
    return registryCategories.reduce((acc, categoryKey) => {
      const matches = [];
      for (const [key, indicator] of sortedIndicatorEntries(categoryKey)) {
        if (
          !seen.has(key) &&
          indicator.displayName.toLowerCase().includes(term)
        ) {
          seen.add(key);
          matches.push(key);
        }
      }
      if (matches.length > 0) acc[categoryKey] = matches;
      return acc;
    }, {});
  }, [searchTerm, registryCategories]);

  // Helper to create indicator object from modal result
  // (shared with the chart context menu — see utils/indicatorInstance.js)
  const createIndicator = useCallback(
    (categoryKey, indicatorKey, options, appearanceOptions) =>
      createIndicatorInstance(
        categoryKey,
        indicatorKey,
        options,
        appearanceOptions,
        chartIndicators,
      ),
    [chartIndicators],
  );

  // Handler to open indicator edit modal
  const handleIndicatorClick = useCallback((categoryKey, indicatorKey) => {
    const indicatorEntry =
      indicatorRegistry[categoryKey].indicators[indicatorKey];
    setModalCategoryKey(categoryKey);
    setModalIndicatorEntry(indicatorEntry);
    // Read the selection from the store — this callback has empty deps, so the
    // rendered `selectedChartId` here would be the one captured on first render.
    const targetChartId = useChartsStore.getState().getSelectedChart()?.id;
    setModalHostBody(getChartHostBody(targetChartId));
    setModalVisible(true);
    setDropdownOpen(false); // Close picker when edit modal opens
  }, []);

  // Handler for modal save: accept both options and appearanceConfig
  const handleModalSave = useCallback(
    (options, appearanceOptions) => {
      if (!selectedChartId || !modalCategoryKey || !modalIndicatorEntry) return;
      const newIndicator = createIndicator(
        modalCategoryKey,
        modalIndicatorEntry.key,
        options,
        appearanceOptions,
      );
      if (newIndicator) addIndicatorToChart(selectedChartId, newIndicator);
    },
    [
      selectedChartId,
      modalCategoryKey,
      modalIndicatorEntry,
      addIndicatorToChart,
      createIndicator,
    ],
  );

  // Compare handlers
  const handleAddCompareSymbol = useCallback(
    async (symbol) => {
      if (!selectedChartId || !symbol) return;

      setCompareLoading(true);
      setCompareError(null);

      try {
        const success = await addComparisonSymbol(selectedChartId, symbol, {
          periodicity: chartPeriodicity,
        });

        if (!success) {
          setCompareError(`Falha ao carregar dados para ${symbol}`);
        } else {
          setCompareSearchTerm("");
          setFilteredCompareSymbols([]);
          setCompareSelectedIndex(0);
        }
      } catch (err) {
        setCompareError(err.message || "Erro ao adicionar simbolo");
      } finally {
        setCompareLoading(false);
      }
    },
    [selectedChartId, chartPeriodicity, addComparisonSymbol],
  );

  const handleRemoveCompareSymbol = useCallback(
    (symbol) => {
      if (selectedChartId) {
        removeComparisonSymbol(selectedChartId, symbol);
      }
    },
    [selectedChartId, removeComparisonSymbol],
  );

  const handleClearAllComparisons = useCallback(() => {
    if (selectedChartId) {
      clearComparisons(selectedChartId);
    }
  }, [selectedChartId, clearComparisons]);

  const handleCompareSearchSubmit = useCallback(
    (e) => {
      e.preventDefault();
      // If there are filtered results and user presses Enter, select the highlighted one
      if (filteredCompareSymbols.length > 0) {
        const selectedSymbol = filteredCompareSymbols[compareSelectedIndex];
        if (selectedSymbol) {
          handleAddCompareSymbol(selectedSymbol.symbol);
          return;
        }
      }
      // Otherwise, try to add the typed symbol directly
      const symbol = compareSearchTerm.trim().toUpperCase();
      if (symbol && symbol !== chartSymbol) {
        handleAddCompareSymbol(symbol);
      }
    },
    [
      compareSearchTerm,
      chartSymbol,
      handleAddCompareSymbol,
      filteredCompareSymbols,
      compareSelectedIndex,
    ],
  );

  // Handle keyboard navigation in compare search
  const handleCompareSearchKeyDown = useCallback(
    (e) => {
      if (filteredCompareSymbols.length === 0) return;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        setCompareSelectedIndex((prev) =>
          prev < filteredCompareSymbols.length - 1 ? prev + 1 : 0,
        );
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setCompareSelectedIndex((prev) =>
          prev > 0 ? prev - 1 : filteredCompareSymbols.length - 1,
        );
      }
    },
    [filteredCompareSymbols.length],
  );

  const toggleDropdown = useCallback(
    (e) => {
      if (e) {
        e.stopPropagation();
      }
      setDropdownOpen((prev) => {
        if (prev) return false;
        setSelectedCategory(favorites.length > 0 ? "favoritos" : null);
        setSearchTerm("");
        setCompareSearchTerm("");
        setCompareError(null);
        setFilteredCompareSymbols([]);
        setCompareSelectedIndex(0);
        return true;
      });
    },
    [favorites.length],
  );

  const handleHide = useCallback(() => {
    setDropdownOpen(false);
  }, []);

  // Handler for category selection
  const handleCategoryClick = useCallback((category) => {
    setSelectedCategory(category);
  }, []);

  // Prepare favorite indicators for rendering
  const favoriteIndicatorList = useMemo(() => {
    return favorites
      .map((indicatorKey) => {
        for (const categoryKey of Object.keys(indicatorRegistry)) {
          const indicator =
            indicatorRegistry[categoryKey].indicators[indicatorKey];
          if (indicator) {
            return { categoryKey, indicatorKey, indicator };
          }
        }
        return null;
      })
      .filter(Boolean);
  }, [favorites]);

  // Pseudo-grupo Silverlight — estático em runtime, referência estável
  const silverlightList = useMemo(() => silverlightIndicatorList(), []);

  // Check if indicator list is scrollable and add visual indicator
  useEffect(() => {
    const checkScrollable = () => {
      const container = indicatorListRef.current?.parentElement;
      const list = indicatorListRef.current;

      if (container && list) {
        const isScrollable = list.scrollHeight > list.clientHeight;
        if (isScrollable) {
          container.classList.add("has-scroll");
        } else {
          container.classList.remove("has-scroll");
          container.classList.remove("scrolled-to-bottom");
        }
      }
    };

    const checkScrollPosition = () => {
      const container = indicatorListRef.current?.parentElement;
      const list = indicatorListRef.current;

      if (container && list) {
        const isAtBottom =
          list.scrollHeight - list.scrollTop - list.clientHeight < 10;
        if (isAtBottom) {
          container.classList.add("scrolled-to-bottom");
        } else {
          container.classList.remove("scrolled-to-bottom");
        }
      }
    };

    checkScrollable();

    const list = indicatorListRef.current;
    if (list) {
      list.addEventListener("scroll", checkScrollPosition);
      const resizeObserver = new ResizeObserver(checkScrollable);
      resizeObserver.observe(list);

      return () => {
        list.removeEventListener("scroll", checkScrollPosition);
        resizeObserver.disconnect();
      };
    }
  }, [selectedCategory, searchTerm, favoriteIndicatorList]);

  return (
    <div className="dropdown">
      <button
        ref={buttonRef}
        className={`navbar-button dropdown-btn${dropdownOpen ? " active" : ""}`}
        onClick={toggleDropdown}
        aria-haspopup="listbox"
        aria-expanded={dropdownOpen}
        data-pr-tooltip={`Estudos (${modKey}+I)`}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.5rem",
          justifyContent: "space-between",
        }}
        data-evo-id="navbar-indicators-dropdown-toggle"
      >
        <div className="navbar-item-content">
          <i className="study-icon navbar-item-icon"></i>
          <span className="navbar-item-label estudos-label">Estudos</span>
          {isCompareMode && (
            <span className="compare-badge navbar-badge">
              {comparisonSymbols.length}
            </span>
          )}
        </div>
        <img
          src={dropdownIconUp}
          alt=""
          style={{
            transform: dropdownOpen ? "rotate(180deg)" : "rotate(0deg)",
          }}
        />
      </button>

      <SimpleModal
        title="Estudos"
        visible={dropdownOpen}
        onHide={handleHide}
        footer={null}
        size="medium"
        className="indicators-picker-modal"
      >
        <div className="indicators-picker-content">
          <div className="drop-context">
            <div className="p-dropdown-header" data-pc-section="header">
              <div
                className="p-dropdown-filter-container"
                data-pc-section="filtercontainer"
              >
                <input
                  ref={searchInputRef}
                  type="text"
                  autoComplete="off"
                  className="p-dropdown-filter p-inputtext p-component"
                  data-pc-section="filterinput"
                  placeholder="Buscar..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  data-evo-id="navbar-indicators-search-input"
                />
                {searchTerm && (
                  <button
                    className="p-dropdown-filter-clear-icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSearchTerm("");
                    }}
                    aria-label="Limpar busca"
                    title="Limpar busca"
                    type="button"
                    data-evo-id="navbar-indicators-search-clear"
                  >
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 14 14"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M8.01186 7.00933L12.27 2.75116C12.341 2.68501 12.398 2.60524 12.4375 2.51661C12.4769 2.42798 12.4982 2.3323 12.4999 2.23529C12.5016 2.13827 12.4838 2.0419 12.4474 1.95194C12.4111 1.86197 12.357 1.78024 12.2884 1.71163C12.2198 1.64302 12.138 1.58893 12.0481 1.55259C11.9581 1.51625 11.8617 1.4984 11.7647 1.50011C11.6677 1.50182 11.572 1.52306 11.4834 1.56255C11.3948 1.60204 11.315 1.65898 11.2488 1.72997L6.99067 5.98814L2.7325 1.72997C2.59553 1.60234 2.41437 1.53286 2.22718 1.53616C2.03999 1.53946 1.8614 1.61529 1.72901 1.74767C1.59663 1.88006 1.5208 2.05865 1.5175 2.24584C1.5142 2.43303 1.58368 2.61419 1.71131 2.75116L5.96948 7.00933L1.71131 11.2675C1.576 11.403 1.5 11.5866 1.5 11.7781C1.5 11.9696 1.576 12.1532 1.71131 12.2887C1.84679 12.424 2.03043 12.5 2.2219 12.5C2.41338 12.5 2.59702 12.424 2.7325 12.2887L6.99067 8.03052L11.2488 12.2887C11.3843 12.424 11.568 12.5 11.7594 12.5C11.9509 12.5 12.1346 12.424 12.27 12.2887C12.4053 12.1532 12.4813 11.9696 12.4813 11.7781C12.4813 11.5866 12.4053 11.403 12.27 11.2675L8.01186 7.00933Z"
                        fill="currentColor"
                      />
                    </svg>
                  </button>
                )}
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 14 14"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="p-icon p-dropdown-filter-icon"
                  data-pc-section="filtericon"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M2.67602 11.0265C3.6661 11.688 4.83011 12.0411 6.02086 12.0411C6.81149 12.0411 7.59438 11.8854 8.32483 11.5828C8.87005 11.357 9.37808 11.0526 9.83317 10.6803L12.9769 13.8241C13.0323 13.8801 13.0983 13.9245 13.171 13.9548C13.2438 13.985 13.3219 14.0003 13.4007 14C13.4795 14.0003 13.5575 13.985 13.6303 13.9548C13.7031 13.9245 13.7691 13.8801 13.8244 13.8241C13.9367 13.7116 13.9998 13.5592 13.9998 13.4003C13.9998 13.2414 13.9367 13.089 13.8244 12.9765L10.6807 9.8328C11.053 9.37773 11.3573 8.86972 11.5831 8.32452C11.8857 7.59408 12.0414 6.81119 12.0414 6.02056C12.0414 4.8298 11.6883 3.66579 11.0268 2.67572C10.3652 1.68564 9.42494 0.913972 8.32483 0.45829C7.22472 0.00260857 6.01418 -0.116618 4.84631 0.115686C3.67844 0.34799 2.60568 0.921393 1.76369 1.76338C0.921698 2.60537 0.348296 3.67813 0.115991 4.84601C-0.116313 6.01388 0.00291375 7.22441 0.458595 8.32452C0.914277 9.42464 1.68595 10.3649 2.67602 11.0265ZM3.35565 2.0158C4.14456 1.48867 5.07206 1.20731 6.02086 1.20731C7.29317 1.20731 8.51338 1.71274 9.41304 2.6124C10.3127 3.51206 10.8181 4.73226 10.8181 6.00457C10.8181 6.95337 10.5368 7.88088 10.0096 8.66978C9.48251 9.45868 8.73328 10.0736 7.85669 10.4367C6.98011 10.7997 6.01554 10.8947 5.08496 10.7096C4.15439 10.5245 3.2996 10.0676 2.62869 9.39674C1.95778 8.72583 1.50089 7.87104 1.31579 6.94046C1.13068 6.00989 1.22568 5.04532 1.58878 4.16874C1.95187 3.29215 2.56675 2.54292 3.35565 2.0158Z"
                    fill="currentColor"
                  ></path>
                </svg>
              </div>
            </div>
            <div className="category-dropdown">
              <ul>
                {categories.map((categoryKey) => (
                  <li
                    key={categoryKey}
                    onClick={() => handleCategoryClick(categoryKey)}
                    className={`dropdown-item${selectedCategory === categoryKey ? " selected" : ""}${categoryKey === "favoritos" ? " favoritos-separator" : ""}${categoryKey === "silverlight" ? " silverlight-separator" : ""}${categoryKey === "comparar" ? " comparar-separator" : ""}`}
                    tabIndex={0}
                    aria-selected={selectedCategory === categoryKey}
                    data-evo-id={`navbar-indicators-category-${categoryKey}`}
                  >
                    {categoryKey === "favoritos" ? (
                      "Favoritos"
                    ) : categoryKey === "silverlight" ? (
                      "Silverlight"
                    ) : categoryKey === "comparar" ? (
                      <>
                        Comparar{" "}
                        {isCompareMode && (
                          <span className="compare-badge">
                            {comparisonSymbols.length}
                          </span>
                        )}
                      </>
                    ) : (
                      indicatorRegistry[categoryKey].displayName
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="indicator-dropdown">
            <div className="indicator-dropdown-header">
              {searchTerm
                ? "Pesquisar"
                : selectedCategory === "favoritos"
                  ? "Favoritos"
                  : selectedCategory === "silverlight"
                    ? "Silverlight"
                    : selectedCategory === "comparar"
                      ? "Comparar Simbolos"
                      : selectedCategory
                        ? indicatorRegistry[selectedCategory]?.displayName
                        : ""}
            </div>
            {/* Favorite indicators */}
            {selectedCategory === "favoritos" &&
              !searchTerm &&
              favoriteIndicatorList.length > 0 && (
                <ul ref={indicatorListRef}>
                  {favoriteIndicatorList.map(
                    ({ categoryKey, indicatorKey, indicator }) => (
                      <li key={indicatorKey} className="indicator-item">
                        <span
                          onClick={() =>
                            handleIndicatorClick(categoryKey, indicatorKey)
                          }
                          style={{ flex: 1, cursor: "pointer" }}
                          data-evo-id={`navbar-indicators-favorite-item-${indicatorKey}`}
                        >
                          {indicator.displayName}
                          {renderSilverlightPill(indicatorKey)}
                        </span>
                        <button
                          className="favorite-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleFavorite(indicatorKey);
                          }}
                          aria-label="Remover dos favoritos"
                          title="Remover dos favoritos"
                          data-evo-id={`navbar-indicators-favorite-remove-${indicatorKey}`}
                        >
                          ★
                        </button>
                      </li>
                    ),
                  )}
                </ul>
              )}

            {/* Pseudo-grupo Silverlight — migrados do app legado; cada item
                continua na categoria REAL (o clique usa categoryKey de origem) */}
            {selectedCategory === "silverlight" && !searchTerm && (
              <ul ref={indicatorListRef}>
                {silverlightList.map(
                  ({ categoryKey, indicatorKey, indicator }) => (
                    <li key={indicatorKey} className="indicator-item">
                      <span
                        onClick={() =>
                          handleIndicatorClick(categoryKey, indicatorKey)
                        }
                        style={{ flex: 1, cursor: "pointer" }}
                        data-evo-id={`navbar-indicators-silverlight-item-${indicatorKey}`}
                      >
                        {indicator.displayName}
                      </span>
                      <button
                        className="favorite-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleFavorite(indicatorKey);
                        }}
                        aria-label={
                          favorites.includes(indicatorKey)
                            ? "Remover dos favoritos"
                            : "Adicionar aos favoritos"
                        }
                        title={
                          favorites.includes(indicatorKey)
                            ? "Remover dos favoritos"
                            : "Adicionar aos favoritos"
                        }
                        data-evo-id={`navbar-indicators-silverlight-favorite-${indicatorKey}`}
                      >
                        {favorites.includes(indicatorKey) ? "★" : "☆"}
                      </button>
                    </li>
                  ),
                )}
              </ul>
            )}

            {/* Compare UI */}
            {selectedCategory === "comparar" && !searchTerm && (
              <div className="compare-panel">
                {/* Search input with lookup */}
                <div className="compare-search-container">
                  <form
                    onSubmit={handleCompareSearchSubmit}
                    className="compare-search-form"
                  >
                    <input
                      ref={compareSearchInputRef}
                      type="text"
                      autoComplete="off"
                      className="compare-search-input"
                      placeholder="Digite o simbolo..."
                      value={compareSearchTerm}
                      onChange={(e) =>
                        setCompareSearchTerm(e.target.value.toUpperCase())
                      }
                      onKeyDown={handleCompareSearchKeyDown}
                      disabled={compareLoading}
                      data-evo-id="navbar-compare-search-input"
                    />
                    <button
                      type="submit"
                      className="compare-search-btn"
                      disabled={compareLoading || !compareSearchTerm.trim()}
                      data-evo-id="navbar-compare-search-submit"
                    >
                      {compareLoading ? "..." : "+"}
                    </button>
                  </form>

                  {/* Symbol lookup dropdown */}
                  {filteredCompareSymbols.length > 0 && (
                    <ul className="compare-lookup-list">
                      {filteredCompareSymbols.map((item, index) => (
                        <li
                          key={item.symbol}
                          className={`compare-lookup-item${index === compareSelectedIndex ? " selected" : ""}`}
                          onClick={() => handleAddCompareSymbol(item.symbol)}
                          onMouseEnter={() => setCompareSelectedIndex(index)}
                          data-evo-id={`navbar-compare-lookup-${item.symbol}`}
                        >
                          <span className="compare-lookup-symbol">
                            {item.I}
                          </span>
                          <span className="compare-lookup-description">
                            {item.D}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {compareError && (
                  <div className="compare-error">{compareError}</div>
                )}

                {/* Current comparisons */}
                {comparisonSymbols.length > 0 && (
                  <div className="compare-current-section">
                    <div className="compare-section-header">
                      <span>Comparando com {chartSymbol}</span>
                      <button
                        className="compare-clear-btn"
                        onClick={handleClearAllComparisons}
                        title="Remover todos"
                        data-evo-id="navbar-compare-clear-all"
                      >
                        Limpar
                      </button>
                    </div>
                    <ul className="compare-current-list">
                      {comparisonSymbols.map(
                        ({ symbol, color, isLoading: symbolLoading }) => (
                          <li key={symbol} className="compare-current-item">
                            <span
                              className="compare-color-box"
                              style={{ backgroundColor: color }}
                            />
                            <span className="compare-symbol-name">
                              {symbol}
                            </span>
                            {symbolLoading && (
                              <span className="compare-loading-indicator">
                                ...
                              </span>
                            )}
                            <button
                              className="compare-remove-btn"
                              onClick={() => handleRemoveCompareSymbol(symbol)}
                              title={`Remover ${symbol}`}
                              data-evo-id={`navbar-compare-remove-${symbol}`}
                            >
                              ×
                            </button>
                          </li>
                        ),
                      )}
                    </ul>
                  </div>
                )}

                {/* Quick add symbols */}
                {availableCompareSymbols.length > 0 && (
                  <div className="compare-quick-section">
                    <div className="compare-section-header">
                      Adicionar rapidamente
                    </div>
                    <div className="compare-quick-list">
                      {availableCompareSymbols.slice(0, 8).map((symbol) => (
                        <button
                          key={symbol}
                          className="compare-quick-btn"
                          onClick={() => handleAddCompareSymbol(symbol)}
                          disabled={compareLoading}
                          data-evo-id={`navbar-compare-quick-${symbol}`}
                        >
                          {symbol}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Category indicators */}
            {selectedCategory &&
              selectedCategory !== "favoritos" &&
              selectedCategory !== "silverlight" &&
              selectedCategory !== "comparar" &&
              !searchTerm && (
                <ul ref={indicatorListRef}>
                  {sortedIndicatorEntries(selectedCategory).map(
                    ([indicatorKey, indicator]) => (
                      <li key={indicatorKey} className="indicator-item">
                        <span
                          onClick={() =>
                            handleIndicatorClick(selectedCategory, indicatorKey)
                          }
                          style={{ flex: 1, cursor: "pointer" }}
                          data-evo-id={`navbar-indicators-category-item-${indicatorKey}`}
                        >
                          {indicator.displayName}
                          {renderSilverlightPill(indicatorKey)}
                        </span>
                        <button
                          className="favorite-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleFavorite(indicatorKey);
                          }}
                          aria-label={
                            favorites.includes(indicatorKey)
                              ? "Remover dos favoritos"
                              : "Adicionar aos favoritos"
                          }
                          title={
                            favorites.includes(indicatorKey)
                              ? "Remover dos favoritos"
                              : "Adicionar aos favoritos"
                          }
                          data-evo-id={`navbar-indicators-category-favorite-${indicatorKey}`}
                        >
                          {favorites.includes(indicatorKey) ? "★" : "☆"}
                        </button>
                      </li>
                    ),
                  )}
                </ul>
              )}

            {/* Search results */}
            {searchTerm && Object.keys(filteredIndicators).length > 0 && (
              <ul ref={indicatorListRef}>
                {Object.entries(filteredIndicators).map(
                  ([categoryKey, indicatorKeys]) => (
                    <React.Fragment key={categoryKey}>
                      {indicatorKeys.map((indicatorKey) => (
                        <li key={indicatorKey} className="indicator-item">
                          <span
                            onClick={() =>
                              handleIndicatorClick(categoryKey, indicatorKey)
                            }
                            style={{ flex: 1, cursor: "pointer" }}
                            data-evo-id={`navbar-indicators-search-item-${indicatorKey}`}
                          >
                            {
                              indicatorRegistry[categoryKey].indicators[
                                indicatorKey
                              ].displayName
                            }
                            {renderSilverlightPill(indicatorKey)}
                          </span>
                          <button
                            className="favorite-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleFavorite(indicatorKey);
                            }}
                            aria-label={
                              favorites.includes(indicatorKey)
                                ? "Remover dos favoritos"
                                : "Adicionar aos favoritos"
                            }
                            title={
                              favorites.includes(indicatorKey)
                                ? "Remover dos favoritos"
                                : "Adicionar aos favoritos"
                            }
                            data-evo-id={`navbar-indicators-search-favorite-${indicatorKey}`}
                          >
                            {favorites.includes(indicatorKey) ? "★" : "☆"}
                          </button>
                        </li>
                      ))}
                    </React.Fragment>
                  ),
                )}
              </ul>
            )}
          </div>
        </div>
      </SimpleModal>

      {/* Indicator Edit Modal */}
      <Suspense fallback={<div></div>}>
        <IndicatorEditModal
          indicatorEntry={modalIndicatorEntry}
          visible={modalVisible}
          onClose={() => setModalVisible(false)}
          onSave={handleModalSave}
          chartIndicators={chartIndicators}
          hostBody={modalHostBody}
        />
      </Suspense>
    </div>
  );
};

export default React.memo(RegistryIndicatorsDropdown);
